import { movies } from "@/mocks/movies";
import { categories } from "@/mocks/categories";
import type { Movie, Category } from "@/types/movie";

export function getMovieById(id: string): Movie | undefined {
  return movies.find((m) => m.id === id);
}

export function getCategoryById(id: string): Category | undefined {
  return categories.find((c) => c.id === id);
}

export function getMoviesByCategory(categoryId: string): Movie[] {
  return movies.filter((m) => m.categoryIds.includes(categoryId));
}

export function getFeaturedMovies(): Movie[] {
  return movies.filter((m) => m.featured);
}

export function getLatestMovies(limit = 10): Movie[] {
  return [...movies].sort((a, b) => b.year - a.year).slice(0, limit);
}

export function getPopularMovies(limit = 10): Movie[] {
  return [...movies].sort((a, b) => b.views - a.views).slice(0, limit);
}

export function searchMovies(keyword: string): Movie[] {
  const kw = keyword.trim().toLowerCase();
  if (!kw) return [];
  return movies.filter((m) =>
    [m.title, m.originalTitle, m.director, ...m.cast, ...m.tags, ...m.genres]
      .join(" ")
      .toLowerCase()
      .includes(kw)
  );
}

export function getMoviesByTag(tag: string): Movie[] {
  const t = tag.trim().toLowerCase();
  if (!t) return [];
  return movies.filter((m) => m.tags.some((x) => x.toLowerCase() === t) || m.genres.some((x) => x.toLowerCase() === t));
}

export function getRelatedMovies(movie: Movie, limit = 5): Movie[] {
  const scored = movies
    .filter((m) => m.id !== movie.id)
    .map((m) => {
      let score = 0;
      m.categoryIds.forEach((c) => {
        if (movie.categoryIds.includes(c)) score += 2;
      });
      m.tags.forEach((t) => {
        if (movie.tags.includes(t)) score += 1;
      });
      m.genres.forEach((g) => {
        if (movie.genres.includes(g)) score += 1;
      });
      if (m.director === movie.director) score += 3;
      return { m, score };
    })
    .sort((a, b) => b.score - a.score || b.m.views - a.m.views);
  return scored.slice(0, limit).map((s) => s.m);
}

export interface MovieFilterState {
  genre: string;
  year: string;
  rating: string;
  sort: string;
}

export const defaultFilterState: MovieFilterState = {
  genre: "全部类型",
  year: "全部年份",
  rating: "全部评分",
  sort: "综合排序",
};

export function applyMovieFilter(list: Movie[], filter: MovieFilterState): Movie[] {
  let result = [...list];

  if (filter.genre && filter.genre !== "全部类型") {
    result = result.filter((m) => m.genres.includes(filter.genre));
  }

  if (filter.year && filter.year !== "全部年份") {
    result = result.filter((m) => m.year === Number(filter.year));
  }

  if (filter.rating && filter.rating !== "全部评分") {
    const threshold = parseFloat(filter.rating);
    if (!Number.isNaN(threshold)) {
      result = result.filter((m) => m.rating >= threshold);
    }
  }

  switch (filter.sort) {
    case "评分最高":
      result.sort((a, b) => b.rating - a.rating);
      break;
    case "年份最新":
      result.sort((a, b) => b.year - a.year);
      break;
    case "最多浏览":
      result.sort((a, b) => b.views - a.views);
      break;
    case "收藏最多":
      result.sort((a, b) => b.favorites - a.favorites);
      break;
    default:
      result.sort((a, b) => b.rating - a.rating || b.views - a.views);
  }

  return result;
}

export function formatViews(views: number): string {
  if (views >= 10000) return `${(views / 10000).toFixed(1)}万`;
  return `${views}`;
}